/* Search-journey view: phase share bar, the interlocking chevron process flow, and the
   insight cards. SVG geometry ported verbatim from artifact.html; the per-stage numbers
   come from the aggregate and insight endpoints. Every node and card filters the table. */

import { $, esc, fmt, scase } from "../util.js";
import { paintIcons } from "../icons.js";
import { JOURNEY_DEF } from "../reference-data.js";

export function renderJourney({ groups, phases, insights }, ctx) {
  const byStage = new Map(groups.map(g => [g.value, g]));
  const stat = j => byStage.get(j) || { n: 0, imp: 0, pos: 0, topQuery: null };
  const total = Math.max(groups.reduce((n, g) => n + g.n, 0), 1);
  const phaseByName = new Map(phases.map(p => [p.phase, p]));

  /* phase share bar */
  const phaseRows = JOURNEY_DEF.map(([phase, icon, col, soft]) => {
    const p = phaseByName.get(phase.toLowerCase()) || { n: 0, imp: 0 };
    return { phase, icon, col, soft, n: p.n, imp: p.imp };
  });
  $("jshare").innerHTML = '<div class="jsharebar">' +
    phaseRows.map(p => p.n ? '<div style="width:' + (p.n / total * 100) + "%;background:" + p.col + '" title="' + p.phase + ": " + fmt(p.n) + '"></div>' : "").join("") + "</div>" +
    '<div class="jsharelegend">' + phaseRows.map(p =>
      '<span><span style="display:inline-block;width:9px;height:9px;border-radius:3px;background:' + p.col +
      ';margin-right:5px;vertical-align:-1px"></span>' + p.phase + " <b>" + fmt(p.n) + "</b> · " + (p.n / total * 100).toFixed(0) + "%</span>").join("") + "</div>";

  /* process flow diagram */
  const PBW = 236, PGAP = 6, TIP = 20, PY = 12, PH = 56, X0 = 6;
  const NW = 208, NH = 68, NSTEP = 80, NY0 = 152;
  let svg = "";
  JOURNEY_DEF.forEach(([phase, icon, col, soft, stages], i) => {
    const x = X0 + i * (PBW + PGAP);
    const cx = x + PBW / 2;
    const ph = phaseRows.find(p => p.phase === phase);
    const notch = i > 0;
    let path = "M " + x + " " + PY +
      " L " + (x + PBW - TIP) + " " + PY +
      " L " + (x + PBW) + " " + (PY + PH / 2) +
      " L " + (x + PBW - TIP) + " " + (PY + PH) +
      " L " + x + " " + (PY + PH);
    path += notch ? " L " + (x + TIP) + " " + (PY + PH / 2) + " Z" : " Z";
    svg += '<path d="' + path + '" fill="' + soft + '" stroke="' + col + '" stroke-width="2"/>';
    const tx = x + (notch ? TIP + 12 : 14);
    svg += '<text x="' + tx + '" y="' + (PY + 24) + '" font-family="Figtree" font-weight="800" font-size="14" fill="' + col + '">' + phase + "</text>" +
      '<text x="' + tx + '" y="' + (PY + 42) + '" font-family="JetBrains Mono" font-size="10" fill="#57575F">' + fmt(ph.n) + " queries · " + fmt(ph.imp) + " im</text>";
    const lastY = NY0 + (stages.length - 1) * NSTEP + NH / 2;
    svg += '<line x1="' + cx + '" y1="' + (PY + PH) + '" x2="' + cx + '" y2="' + lastY + '" stroke="' + col + '" stroke-width="1.5" stroke-dasharray="3 4" opacity=".55"/>';

    stages.forEach(([j, blurb], si) => {
      const st = stat(j);
      const nx = cx - NW / 2, ny = NY0 + si * NSTEP;
      const topQ = st.topQuery;
      svg += '<g class="jfnode" data-jf="' + j + '" style="cursor:pointer">' +
        '<rect x="' + nx + '" y="' + ny + '" width="' + NW + '" height="' + NH + '" rx="12" fill="#FFFFFF" stroke="' + col + '" stroke-width="1.6"/>' +
        '<circle cx="' + cx + '" cy="' + ny + '" r="3.2" fill="' + col + '"/>' +
        '<text x="' + (nx + 12) + '" y="' + (ny + 21) + '" font-family="Figtree" font-weight="700" font-size="12.5" fill="#0A0A0B">' + scase(j) + "</text>" +
        '<text x="' + (nx + NW - 12) + '" y="' + (ny + 22) + '" text-anchor="end" font-family="JetBrains Mono" font-weight="800" font-size="14" fill="' + col + '">' + fmt(st.n) + "</text>" +
        '<text x="' + (nx + 12) + '" y="' + (ny + 37) + '" font-family="JetBrains Mono" font-size="9" fill="#57575F">' + fmt(st.imp) + " im · pos " + (st.n ? st.pos.toFixed(1) : "—") + "</text>" +
        '<text x="' + (nx + 12) + '" y="' + (ny + 53) + '" font-family="JetBrains Mono" font-size="8.5" fill="#8A8A93">' +
        esc(topQ ? '"' + (topQ.length > 30 ? topQ.slice(0, 30) + "…" : topQ) + '"' : blurb) + "</text>" +
        "<title>" + scase(j) + " — " + blurb + ": " + fmt(st.n) + " queries, " + fmt(st.imp) + " impressions" +
        (topQ ? '. Top: "' + esc(topQ) + '"' : "") + "</title></g>";
    });
  });
  $("jflow").innerHTML = svg;
  document.querySelectorAll(".jfnode").forEach(el =>
    el.addEventListener("click", () => ctx.onFilter({ journey: el.dataset.jf })));

  /* insight cards */
  const colorFor = j => {
    const phase = JOURNEY_DEF.find(([, , , , stages]) => stages.some(([s]) => s === j));
    return phase ? phase[2] : "var(--red)";
  };
  const ICONS_FOR = { largest: "up", weakest: "alert", zeroClick: "click" };
  $("jinsights").innerHTML = insights.map(i => {
    const col = i.journey ? colorFor(i.journey) : "var(--red)";
    const headline = i.kind === "largest" ? scase(i.journey) + " — " + fmt(i.value) + " queries"
      : i.kind === "weakest" ? scase(i.journey) + " averages position " + i.value
      : fmt(i.value) + " journey queries get impressions but no clicks";
    return '<div class="jins"' + (i.journey ? ' data-jf2="' + i.journey + '"' : "") + ">" +
      '<span style="position:absolute;top:0;left:0;bottom:0;width:3px;background:' + col + '"></span>' +
      '<div class="jt"><i data-ic="' + (ICONS_FOR[i.kind] || "up") + '"></i>' + i.label + "</div>" +
      '<div class="jvv">' + esc(headline) + '</div><div class="jss">' + esc(i.detail) + "</div></div>";
  }).join("");
  paintIcons($("jinsights"));
  document.querySelectorAll("[data-jf2]").forEach(el =>
    el.addEventListener("click", () => ctx.onFilter({ journey: el.dataset.jf2 })));
}
